import { useCallback } from 'react';

import { getTokenValue, useThemeObserver } from '@atlaskit/tokens';

import { getLightnessPercentage, increaseLightness, reduceLightness } from '../color-utils';

const BACKGROUND_LIGHTNESS_MAX = 35;
const TEXT_LIGHTNESS_MIN = 90;

export const getDarkModeBackgroundColor = (lightModeColor: string): string => {
	if (lightModeColor === '#FFFFFF') {
		return getTokenValue('elevation.surface.overlay', '#1D2125');
	} else {
		const lightnessPercentage = getLightnessPercentage(lightModeColor);
		if (lightnessPercentage === undefined) {
			return lightModeColor;
		}
		return lightnessPercentage > BACKGROUND_LIGHTNESS_MAX
			? reduceLightness(lightModeColor, BACKGROUND_LIGHTNESS_MAX)
			: lightModeColor;
	}
};

export const useDarkModeColors = (
	isSSRProductInDarkMode?: boolean,
): {
	getDarkModeCompatibleBackgroundColor: (backgroundColor: string) => string;
	getDarkModeCompatibleTextColor: (textColor: string) => string;
	shouldEvaluateBackgroundLightness: boolean;
} => {
	const isSpaAkThemeInDarkMode = useThemeObserver().colorMode === 'dark';
	const shouldEvaluateLightness =
		process.env.REACT_SSR && isSSRProductInDarkMode !== undefined
			? isSSRProductInDarkMode
			: isSpaAkThemeInDarkMode;

	const getDarkModeCompatibleBackgroundColor = useCallback(
		(backgroundColor: string): string => {
			return shouldEvaluateLightness
				? getDarkModeBackgroundColor(backgroundColor)
				: backgroundColor;
		},
		[shouldEvaluateLightness],
	);

	const getDarkModeCompatibleTextColor = useCallback(
		(textColor: string): string => {
			return shouldEvaluateLightness ? increaseLightness(textColor, TEXT_LIGHTNESS_MIN) : textColor;
		},
		[shouldEvaluateLightness],
	);

	return {
		shouldEvaluateBackgroundLightness: shouldEvaluateLightness,
		getDarkModeCompatibleBackgroundColor,
		getDarkModeCompatibleTextColor,
	};
};
